import { createReducer, on } from '@ngrx/store';
import { AdminUser, AdminUserTenant } from 'src/app/shared/api/types/GraphQL';
import { JwtRole } from 'src/app/shared/api/types/types';
import { ACTIVE_TENANT_KEY, persistActiveTenant, resolveActiveTenant } from 'src/app/shared/utils/tenant-storage.util';
import * as AuthActions from './auth.actions';

export interface State {
  isInitializing: boolean;
  loading: boolean;
  user: { email?: string | null };
  adminUser: AdminUser | null;
  userId: string;
  tenants: AdminUserTenant[];
  activeTenant: AdminUserTenant;
  adminTenantId: string;
  error: Error;
  roles: JwtRole[];
  /** Whether the token's role claim has been read — see setViewerRoles below. */
  rolesResolved: boolean;
  appLanguage: string;
  verificationCode: string;
}

const initialState: State = {
  isInitializing: true,
  loading: false,
  user: null,
  adminUser: null,
  userId: '',
  tenants: [],
  activeTenant: null,
  adminTenantId: '',
  error: null,
  roles: [],
  rolesResolved: false,
  appLanguage: 'EN',
  verificationCode: ''
};

export const authReducer = createReducer(
  initialState,
  on(AuthActions.loginStart, (state) => ({
    ...state,
    loading: true
  })),
  on(AuthActions.login, (state, { user }) => ({
    ...state,
    user,
    isInitializing: false,
    loading: true
  })),
  on(AuthActions.loginVerificationStart, (state) => ({
    ...state,
    loading: false
  })),
  on(AuthActions.loginVerificationSend, (state, { verificationCode }) => ({
    ...state,
    loading: true,
    verificationCode
  })),
  on(AuthActions.autoLogin, (state, { user }) => ({
    ...state,
    user,
    isInitializing: false,
    loading: false
  })),
  on(AuthActions.loginError, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),
  on(AuthActions.logout, (state) => {
    // Only the in-session key goes: where the user last worked is the account's
    // `lastTenant` preference, which the next login of that user reads (NBO-2517).
    localStorage.removeItem(ACTIVE_TENANT_KEY);
    return {
      ...state,
      user: null,
      adminUser: null,
      tenants: [],
      activeTenant: null,
      isInitializing: false
    };
  }),
  on(AuthActions.setTenants, (state, { tenants, adminUser, lastTenantId }) => {
    // NBO-2368 / NBO-2517: this session's tenant, else the account's last
    // tenant (from the same login answer), else the default flag, else the first.
    const activeTenant = resolveActiveTenant(tenants, {
      session: localStorage.getItem(ACTIVE_TENANT_KEY),
      lastTenant: lastTenantId
    });
    if (activeTenant) {
      // Self-heals a stale id (revoked access) for the rest of the session. The
      // account row follows from the active tenant (LastTenantPreferenceService).
      persistActiveTenant(activeTenant.id);
    }
    return {
      ...state,
      tenants,
      activeTenant,
      adminUser,
      loading: false
    };
  }),
  // rolesResolved, not `roles.length`: an empty array is a legitimate answer
  // (a user with no roles), so the count cannot distinguish "none" from "not
  // known yet". The shell rail gates its tree on this flag — without it a
  // role-less user would sit in front of an empty rail forever (NBO-2331 r4).
  on(AuthActions.setViewerRoles, (state, { roles }) => ({
    ...state,
    roles,
    rolesResolved: true
  })),
  on(AuthActions.setActiveTenant, (state, { tenant }) => ({
    ...state,
    activeTenant: tenant,
    loading: false
  })),
  on(AuthActions.setAppLanguage, (state, { language }) => ({
    ...state,
    appLanguage: language
  })),
  on(AuthActions.setAdminTenant, (state, { adminTenantId }) => ({
    ...state,
    adminTenantId
  })),
  on(AuthActions.setUserId, (state, { userId }) => ({
    ...state,
    userId
  }))
);
